import { v4 as uuidv4 } from "uuid";
import { EventSystem } from "./EventSystem";
import { toRadians } from "./Utils";
import { getModuleLog } from "./Logger";

export type ListenerFunction = (result: OrientationResult) => void;

type Listener = {
    uid: string;
    callback: ListenerFunction;
};

export type OrientationResult = {
    heading: number;
    transform: string;
};

const TIME_DELTA_THRESHOLD = 0.25; // maximum time difference between iterations, s
const ANGLE_DELTA_THRESHOLD = 0.1; // minimum rotation change to be redrawn, deg

const INERTIA_MOMENT_DEFAULT = 0.2; // default physical properties
const ALPHA_DEFAULT = 10;
const MB_DEFAULT = 1000;

const ANIMATION_FREQ_MS = 50;

let time1 = 0,
    time2 = 0; // timestamps of previous iterations--used in numerical integration
let angle1 = 0,
    angle2 = 0,
    angle0 = 0; // angles of previous iterations
let angleLastDrawn: number; // last drawn angular position
let animationOn = false; // if animation should be performed

let inertiaMoment = INERTIA_MOMENT_DEFAULT; // moment of inertia
let alpha = ALPHA_DEFAULT; // damping coefficient
const mB = MB_DEFAULT; // magnetic field coefficient

let events: EventSystem | undefined;
let listeners: Listener[] = [];
let listenerRunning = false;
let animationTimer: any;
let permissionRequested = false;

function logger() {
    return getModuleLog("orientation");
}

/**
 * Use this to set physical properties.
 * Negative values will be replaced by default values
 *
 * @param inertiaMoment Moment of inertia (default 0.1)
 * @param alpha             Damping coefficient (default 10)
 */
export function setPhysical(im: number, a: number) {
    inertiaMoment = im >= 0 ? im : INERTIA_MOMENT_DEFAULT;
    alpha = a >= 0 ? a : ALPHA_DEFAULT;
}

/**
 * Use this to set new "magnetic field" angle at which image should rotate
 *
 * @param   angleNew    new magnetic field angle, deg., relative to vertical axis.
 * @param   animate     true, if image shoud rotate using animation, false to set new rotation instantly
 */
function rotationUpdate(angleNew: number, animate: boolean) {
    if (animate) {
        if (Math.abs(angle0 - angleNew) > ANGLE_DELTA_THRESHOLD) {
            angle0 = angleNew;
        }
        animationOn = true;
    } else {
        angle1 = angleNew;
        angle2 = angleNew;
        angle0 = angleNew;
        angleLastDrawn = angleNew;
        animationOn = false;
    }
}

/**
 * Recalculate angles using equation of dipole circular motion
 *
 * @param   timeNew     timestamp of method invoke
 * @return              if there is a need to redraw rotation
 */
function angleRecalculate(timeNew: number) {
    // recalculate angle using simple numerical integration of motion equation
    let deltaT1 = (timeNew - time1) / 1000;
    if (deltaT1 > TIME_DELTA_THRESHOLD) {
        deltaT1 = TIME_DELTA_THRESHOLD;
        time1 = timeNew + Math.round(TIME_DELTA_THRESHOLD * 1000);
    }
    let deltaT2 = (time1 - time2) / 1000;
    if (deltaT2 > TIME_DELTA_THRESHOLD) {
        deltaT2 = TIME_DELTA_THRESHOLD;
    }

    // circular acceleration coefficient
    const koefI = inertiaMoment / deltaT1 / deltaT2;

    // circular velocity coefficient
    const koefAlpha = alpha / deltaT1;

    // angular momentum coefficient
    const koefk =
        mB *
        (Math.sin(toRadians(angle0)) * Math.cos(toRadians(angle1)) -
            Math.sin(toRadians(angle1)) * Math.cos(toRadians(angle0)));

    const angleNew =
        (koefI * (angle1 * 2 - angle2) + koefAlpha * angle1 + koefk) / (koefI + koefAlpha);

    // reassign previous iteration variables
    angle2 = angle1;
    angle1 = angleNew;
    time2 = time1;
    time1 = timeNew;

    // if angles changed less then threshold, return false - no need to redraw the view
    if (Math.abs(angleLastDrawn - angle1) < ANGLE_DELTA_THRESHOLD) {
        return false;
    } else {
        angleLastDrawn = angle1;
        return true;
    }
}

export function initOrientation(eventSystem: EventSystem) {
    events = eventSystem;
}

function updateListener() {
    const shouldBeRunning = listeners.length > 0;
    if (listenerRunning && !shouldBeRunning) {
        stop();
    } else if (!listenerRunning && shouldBeRunning) {
        start();
    }
}

function onOrientationChanged(e: any) {
    logger().debug(`orientation changed : ${e.webkitCompassHeading}`);
    const heading = e.webkitCompassHeading || Math.abs(e.alpha - 360);
    rotationUpdate(heading, true);
}

function animateOrientation() {
    let heading: number | undefined;
    if (animationOn) {
        if (angleRecalculate(new Date().getTime())) {
            heading = angle1;
        }
    } else {
        heading = angle1;
    }

    if (heading) {
        const transform = `rotate(${-heading}deg)`;
        const result = { heading, transform } as OrientationResult;

        events?.raiseEvent("ds-orientation-changed", result);
        listeners.forEach((x) => x.callback(result));
    }
}

function startAnimationTimeout() {
    animationTimer = setInterval(() => {
        animateOrientation();
    }, ANIMATION_FREQ_MS);
}

function start() {
    if (!listenerRunning && !permissionRequested) {
        logger().info("starting listener");
        // IOS needs to request permission to read orientation. Rather than check specially for IOS
        // we try to call it and if it throws we then will fallback to directly attaching to the
        // deviceorientationabsolute event which is used on Android.
        try {
            permissionRequested = true;
            (DeviceOrientationEvent as any)
                .requestPermission()
                .then((response: any) => {
                    if (response === "granted") {
                        logger().info("DeviceOrientationEvent permission granted");
                        window.addEventListener("deviceorientation", onOrientationChanged, true);
                        startAnimationTimeout();
                        listenerRunning = true;
                    } else {
                        logger().error("DeviceOrientationEvent permission denied");
                    }
                })
                .catch(() => {
                    logger().error("DeviceOrientationEvent permission not supported");
                })
                .finally(() => {
                    permissionRequested = false;
                });
        } catch (_e) {
            logger().info("using deviceorientationabsolute event");
            window.addEventListener("deviceorientationabsolute", onOrientationChanged, true);
            startAnimationTimeout();
            listenerRunning = true;
            permissionRequested = false;
        }
    }
}

function stop() {
    if (listenerRunning) {
        clearTimeout(animationTimer);
        animationTimer = undefined;
        window.removeEventListener("deviceorientation", onOrientationChanged);
        window.removeEventListener("deviceorientationabsolute", onOrientationChanged);
        listenerRunning = false;
    }
}

export function registerOrientationListener(callback: ListenerFunction): string {
    const uid = uuidv4();
    listeners.push({
        uid,
        callback,
    });
    updateListener();
    logger().info(`listener added ${uid}`);
    return uid;
}

export function unregisterOrientationListener(uid: string) {
    listeners = listeners.filter((x) => x.uid !== uid);
    logger().info(`listener removed ${uid}`);
    updateListener();
}
